<template>
  <div class="artdecorbutton-wrapper">
    <v-tooltip content-class="d-block" :disabled="!showHint" top>
      <template v-slot:activator="{ on: tooltip }">
        <v-btn
          v-if="!listItem && !link && !linkExternal"
          v-on="{ ...tooltip, ...$listeners }"
          class="white--text"
          :class="{'artdecorbutton': !noMarginRight}"
          :disabled="disabled"
          :loading="loading"
          :color="parsedColor"
          :text="!showBackgroundColor"
          :icon="list"
          :href="href"
          :target="target"
          :to="to"
          :small="small"
          :x-small="xSmall"
          :fab="fab"
          :outlined="outlined"
          :type="submit ? 'submit' : undefined"
        >
          <slot>
            <span v-if="showText" v-text="parsedText"/>
          </slot>
          <template #loader>
            <slot name="loader"></slot>
          </template>

          <v-icon
            v-if="parsedIcon"
            :right="showText"
            v-text="parsedIcon"
            :class="spinning ? 'mdi-spin' : ''"
            :size="parsedIconSize"
          />
        </v-btn>

        <component
          v-else-if="link && to"
          :is="disabled ? 'span' : 'router-link'"
          :class="disabled ? 'text--disabled' : `d-inline-flex align-center mr-1 ${parsedColor}--text text-decoration-none font-weight-medium`"
          :to="to"
        >
          <slot>
            {{ parsedText }}
          </slot>
        </component>

        <!-- Routerlink External in new tab/window -->
        <component
          v-else-if="linkExternal && to"
          :is="disabled ? 'span' : 'router-link'"
          :class="disabled ? 'text--disabled' : `d-inline-flex align-center mr-1 ${parsedColor}--text text-decoration-none font-weight-medium`"
          :to="to"
          target="_blank"
        >
          <slot>
            {{ parsedText }}
          </slot>
          <v-icon
            v-if="parsedIcon && !disabled"
            :right="showText"
            v-text="parsedIcon"
            :size="parsedIconSize"
            :color="parsedColor"
          />
        </component>

        <button
          v-else-if="link && !to"
          :class="`d-inline-flex align-center mr-1 ${parsedColor}--text text-decoration-none font-weight-medium`"
          v-on="$listeners"
        >
          <slot>
            {{ parsedText }}
          </slot>
        </button>

        <a
          v-else-if="linkExternal && !to"
          :class="`d-inline-flex align-baseline mr-1 ${parsedColor}--text text-decoration-none font-weight-medium`"
          :href="href"
          target="_blank"
        >
          <slot>{{ parsedText }}</slot>
          <v-icon
            v-if="parsedIcon"
            :right="showText"
            v-text="parsedIcon"
            :size="parsedIconSize"
            :color="parsedColor"
          />
        </a>

        <v-list-item
          v-else
          v-on="{ ...tooltip, ...$listeners }"
          :color="parsedColor"
          :disabled="disabled"
        >
          <v-list-item-content>
            <v-list-item-title :class="`${parsedColor}--text`">
              <slot>
                <span v-if="showText" v-text="parsedText"/>
              </slot>

              <v-icon
                v-if="parsedIcon"
                :right="showText"
                v-text="parsedIcon"
                :size="parsedIconSize"
                :color="parsedColor"
              />
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <span v-if="showHint" v-text="parsedHint"/>
    </v-tooltip>
  </div>

</template>

<script>
export default {
  name: 'AdButton',

  /**
   * Properties.
   */
  props: {
    type: {
      type: String,
      required: false
    },
    text: {
      type: String,
      required: false
    },
    hint: {
      type: String,
      required: false
    },
    icon: {
      type: String,
      required: false
    },
    color: {
      type: String,
      required: false
    },
    admin: {
      type: Boolean,
      required: false,
      default: false
    },
    list: {
      type: Boolean,
      required: false,
      default: false
    },
    medium: {
      type: Boolean,
      required: false,
      default: false
    },
    larger: {
      type: Boolean,
      required: false,
      default: false
    },
    fab: {
      type: Boolean,
      required: false,
      default: false
    },
    outlined: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    spinning: {
      type: Boolean,
      required: false,
      default: false
    },
    small: {
      type: Boolean,
      required: false,
      default: false
    },
    xSmall: {
      type: Boolean,
      required: false,
      default: false
    },
    submit: {
      type: Boolean,
      required: false,
      default: false
    },
    listItem: {
      type: Boolean,
      required: false,
      default: false
    },
    to: {
      type: [String, Object],
      required: false
    },
    href: {
      type: String,
      required: false
    },
    target: {
      type: String,
      required: false
    },
    link: {
      type: Boolean,
      required: false,
      default: false
    },
    linkExternal: {
      type: Boolean,
      required: false,
      default: false
    },
    noMarginRight: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  /**
   * Data variables.
   */
  data () {
    return {
      configurations: {
        add: {
          text: 'add',
          hint: 'add',
          icon: 'mdi-plus',
          color: 'orange'
        },
        quickadd: {
          text: 'quick-add',
          hint: 'quick-add',
          icon: 'mdi-playlist-edit',
          color: 'orange'
        },
        filter: {
          text: 'filter',
          hint: 'filter',
          icon: 'mdi-filter',
          color: 'green'
        },
        'change-status': {
          text: 'change-status',
          hint: 'change-status',
          icon: 'mdi-state-machine',
          color: 'orange'
        },
        new: {
          text: 'new',
          hint: 'new-template-ref',
          icon: 'mdi-link-variant',
          color: 'orange'
        },
        diagram: {
          text: 'diagram',
          hint: 'show-diagram',
          icon: 'mdi-lan mdi-flip-v',
          color: 'green'
        },
        graph: {
          text: 'graph',
          hint: 'show-hierarchical-graph',
          icon: 'mdi-sort-variant',
          color: 'green'
        },
        table: {
          text: 'table',
          hint: 'table',
          icon: 'mdi-table',
          color: 'green'
        },
        edit: {
          text: 'edit',
          hint: 'edit',
          icon: 'mdi-lead-pencil',
          color: 'orange'
        },
        clone: {
          text: 'clone',
          hint: 'clone',
          icon: 'mdi-content-duplicate',
          color: 'orange'
        },
        temple: {
          text: 'temple',
          hint: 'edit-temple',
          icon: 'mdi-bank',
          color: 'orange'
        },
        issue: {
          text: 'issue',
          hint: 'issue',
          icon: 'mdi-flag',
          color: 'red'
        },
        validate: {
          text: 'validate',
          hint: 'validate',
          icon: 'mdi-check',
          color: 'green'
        },
        cancel: {
          text: 'cancel',
          hint: 'cancel',
          icon: 'mdi-cancel',
          color: 'grey'
        },
        save: {
          text: 'save',
          hint: 'save',
          icon: 'mdi-content-save',
          color: 'orange'
        },
        'save-and-close': {
          text: 'save-and-close',
          hint: 'save-and-close',
          icon: 'mdi-content-save',
          color: 'orange'
        },
        ok: {
          text: 'ok',
          hint: 'ok',
          icon: 'mdi-check',
          color: 'orange'
        },
        delete: {
          text: 'delete',
          hint: 'delete',
          icon: 'mdi-delete',
          color: 'red'
        },
        actions: {
          text: 'Actions',
          hint: 'Actions on this item',
          icon: 'mdi-cog',
          color: 'orange'
        },
        maintenance: {
          text: 'maintenance',
          hint: 'maintenance',
          icon: 'mdi-wrench',
          color: 'orange'
        },
        generate: {
          text: 'generate',
          hint: 'generate',
          icon: 'mdi-ruler-square-compass',
          color: 'orange'
        },
        refresh: {
          text: 'refresh',
          hint: 'refresh',
          icon: 'mdi-refresh',
          color: 'green'
        },
        back2list: {
          text: 'return-to-list',
          hint: 'return-to-list',
          icon: 'mdi-backburger',
          color: 'green'
        },
        'view-table': {
          text: 'view',
          hint: 'view',
          icon: 'mdi-view-list',
          color: 'green'
        },
        'view-dashboard': {
          text: 'view',
          hint: 'view',
          icon: 'mdi-view-dashboard',
          color: 'green'
        },
        'view-details': {
          text: 'view',
          hint: 'view-details',
          icon: 'mdi-text',
          color: 'green'
        },
        login: {
          text: 'login',
          hint: 'login',
          icon: 'mdi-login',
          color: 'green'
        },
        more: {
          text: 'more',
          hint: 'showmoremetadata',
          icon: 'mdi-chevron-up',
          color: 'green'
        },
        moremeta: {
          text: 'more',
          hint: 'showmore-meta',
          icon: 'mdi-more',
          color: 'green'
        },
        less: {
          text: 'less',
          hint: 'showless',
          icon: 'mdi-chevron-up',
          color: 'green'
        },
        expand: {
          text: 'expand',
          hint: 'expand',
          icon: 'mdi-arrow-expand-all',
          color: 'green lighten-2'
        },
        collapse: {
          text: 'collapse',
          hint: 'collapse',
          icon: 'mdi-arrow-collapse-all',
          color: 'green lighten-2'
        },
        erase: {
          text: 'delete',
          hint: 'delete',
          icon: 'mdi-window-close',
          color: 'red lighten-2'
        },
        lighterase: {
          text: 'delete',
          hint: 'delete',
          icon: 'mdi-window-close',
          color: 'red lighten-3'
        },
        close: {
          text: 'close',
          hint: 'close',
          icon: 'mdi-close-circle-outline',
          color: 'green lighten-2'
        },
        compare: {
          text: 'diff-artefact',
          hint: 'diff-artefact',
          icon: 'mdi-delta',
          color: 'green'
        },
        'speed-dial': {
          text: '',
          hint: '',
          icon: 'mdi-dots-vertical',
          color: 'grey'
        },
        languages: {
          text: 'languages',
          hint: '',
          icon: 'mdi-web',
          color: 'orange'
        },
        move: {
          text: 'move-items',
          hint: 'move-items',
          icon: 'mdi-cursor-move',
          color: 'orange'
        },
        moveConceptUp: {
          text: 'move-concept',
          hint: 'move-concept',
          icon: 'mdi-arrow-up',
          color: 'orange'
        },
        moveConceptDown: {
          text: 'move-concept',
          hint: 'move-concept',
          icon: 'mdi-arrow-down',
          color: 'orange'
        },
        'new-issue': {
          text: 'new-issue',
          hint: 'new-issue',
          icon: 'mdi-flag',
          color: 'orange'
        },
        map: {
          text: 'add-map',
          hint: 'add-map',
          icon: 'mdi-map-legend',
          color: 'orange'
        },
        association: {
          text: 'add-association',
          hint: 'add-association',
          icon: 'mdi-map-legend',
          color: 'orange'
        },
        'new-map': {
          text: 'new-map',
          hint: 'new-map',
          icon: 'mdi-map-legend',
          color: 'orange'
        },
        'new-assignment': {
          text: 'new-assignment',
          hint: 'new-assignment',
          icon: 'mdi-account',
          color: 'orange'
        },
        'new-event': {
          text: 'new-event',
          hint: 'new-event',
          icon: 'mdi-text',
          color: 'orange'
        },
        'de-inherit': {
          text: 'de-inherit',
          hint: 'de-inherit-concept',
          icon: 'mdi-link-variant-off',
          color: 'orange'
        },
        inherit: {
          text: 'inherit',
          hint: 'inherit-concept',
          icon: 'mdi-link-variant',
          color: 'orange'
        },
        link: {
          text: 'link',
          hint: 'go-to',
          color: 'green'
        },
        linkExternal: {
          text: 'link',
          hint: 'go-to',
          icon: 'mdi-open-in-new',
          color: 'green'
        },
        undo: {
          text: '',
          hint: 'undo',
          icon: 'mdi-undo-variant',
          color: 'orange'
        },
        redo: {
          text: '',
          hint: 'redo',
          icon: 'mdi-redo-variant',
          color: 'orange'
        },
        notify: {
          text: '',
          hint: 'notify',
          icon: 'mdi-email-send',
          color: 'orange'
        },
        info: {
          text: '',
          hint: 'info',
          icon: 'mdi-information-outline',
          color: 'blue'
        },
        license: {
          text: '',
          hint: 'license',
          icon: 'mdi-license',
          color: 'blue'
        },
        expansionset: {
          text: 'expansion-set',
          hint: 'view-expansion-set',
          icon: 'mdi-camera-plus-outline',
          color: 'green'
        },
        copy: {
          text: 'copy',
          hint: 'copy',
          icon: 'mdi-content-copy',
          color: 'grey darken-2'
        },
        download: {
          text: 'download',
          hint: 'download',
          icon: 'mdi-download',
          color: 'green'
        },
        downloads: {
          text: 'downloads',
          hint: 'downloads',
          icon: 'mdi-download',
          color: 'green'
        },
        render: {
          text: 'render',
          hint: 'render',
          icon: 'mdi-clipboard-text-play',
          color: 'green'
        },
        fullscreen: {
          text: 'full-screen',
          hint: 'full-screen',
          icon: 'mdi-fullscreen',
          color: 'green'
        },
        'exit-fullscreen': {
          text: 'full-screen-exit',
          hint: 'full-screen-exit',
          icon: 'mdi-fullscreen-exit',
          color: 'green'
        },
        'table-compact': {
          text: 'table-compact',
          hint: 'table-compact',
          icon: 'mdi-arrow-collapse-vertical',
          color: 'green'
        },
        'table-normal': {
          text: 'table-normal',
          hint: 'table-normal',
          icon: 'mdi-arrow-expand-vertical',
          color: 'green'
        },
        preflight: {
          text: 'preflight',
          hint: 'preflight',
          icon: 'mdi-check-decagram',
          color: 'green'
        },
        reassign: {
          text: 'reassign',
          hint: 'reassign',
          icon: 'mdi-table-refresh',
          color: 'green'
        },
        'tag-on': {
          text: 'tags-show',
          hint: 'tags-show',
          icon: 'mdi-tag-outline',
          color: 'green'
        },
        'tag-off': {
          text: 'tags-hide',
          hint: 'tags-hide',
          icon: 'mdi-tag-off-outline',
          color: 'green'
        },
        unmatch: {
          text: 'concept-maps-create-unmatched-button',
          hint: 'concept-maps-create-unmatched-button',
          icon: 'mdi-power-plug-off',
          color: 'orange'
        }
      }
    }
  },

  computed: {
    /**
     * Get current matching configuration.
     *
     * @return {{}}
     */
    configuration () {
      // Return current configuration.
      return this.configurations?.[this.type] || (this.linkExternal ? this.configurations?.linkExternal : null) || (this.link ? this.configurations?.link : null)
    },

    /**
     * Get parsed text.
     *
     * @return {string}
     */
    parsedText () {
      // Return text from property or configuration.
      return (
        this.text || this.$t(this.configuration?.text) || this.type || 'button'
      )
    },

    /**
     * Get parsed hint.
     *
     * @return {string}
     */
    parsedHint () {
      // Return hint from property or configuration.
      return this.hint || this.$t(this.configuration?.hint) || null
    },

    /**
     * Get parsed icon to show.
     *
     * @return {string}
     */
    parsedIcon () {
      // Return icon from property or configuration.
      return this.icon || this.configuration?.icon || null
    },

    /**
     * Get parsed icon to show.
     *
     * @return {string}
     */
    parsedIconSize () {
      // Return icon size in base of small or list mode is active or not.
      // If not, see if icon shall be larger or not
      if (this.xSmall === true) return '12'
      if (this.small === true) return '16'
      if (this.medium === true) return '20'
      if (this.larger === true) return '22'
      if (this.list === true) return '26'
      return '16'
    },

    /**
     * Get parsed color.
     *
     * @return {string}
     */
    parsedColor () {
      // Get color from property or configuration.
      const color = [this.color || this.configuration?.color || 'primary']
      // Check if admin property is true
      if (this.admin === true) {
        // Push lighten-2 class.
        color.push('lighten-2')
      }

      // Return joined color information.
      return color.join(' ')
    },

    /**
     * Check if button should have background color or not.
     *
     * @return {boolean}
     */
    showBackgroundColor () {
      // Return state if admin or fab mode is active.
      return this.admin === true || this.fab === true
    },

    /**
     * Get state if text should shown.
     *
     * @return {boolean}
     */
    showText () {
      // Only show text if not list or fab mode is active.
      return this.list === false && this.fab === false
    },

    /**
     * Get state if hint should shown.
     *
     * @return {boolean}
     */
    showHint () {
      return !!this.parsedHint === true
    }

    /**
    * get style for to links
     */
    // linkStyle () {
    //   return this.link ? 'text-transform: none !important; letter-spacing: normal !important;' : ''
    // }
  }
}
</script>

<style lang="scss" scoped>
.v-btn {
  min-width: 0 !important;
}

::v-deep .artdecorbutton {
  margin-right: 16px;
}

.artdecorbutton-wrapper {
  display: inline-block;
}

.v-menu__content .artdecorbutton-wrapper {
  display: initial;
}
.v-btn--router::before {
  opacity: 0 !important;
}
</style>
